/* Copyright (c) 2020-present CloudIO Inc */

import { GenericDataRecord, RootRequest } from '@cloudio-saas/datasource-types';
import {
  atom,
  selector,
  useStateXAtomValue,
  useStateXSelector,
} from '@cloudio-saas/statex';
import type { Platform } from 'componentregistry/platform/Platform';
import { hideSidebarAtom } from 'designer/atoms';
import { useMemo } from 'react';
import { isEmpty } from 'utils/isEmpty';
import {
  userCSVDateFormatAtom,
  userCSVDateTimeFormatAtom,
  userDateFormatAtom,
  userDateTimeFormatAtom,
  userNumberFormatAtom,
  userTimeZoneAtom,
} from 'utils/state';
import { routeAtom } from '../routes/RouteState';
import { usePlatform } from 'platform/PlatformHooks';

export interface UserRole {
  appUid: string;
  roleUid: string;
}

export interface UserSession {
  sessionId: string;
  userName: string;
  orgUid: string;
  displayName: string;
  isAuthenticated?: boolean;
  x: string;
  jwt: string;
  status: 'OK';
  devApps?: string[];
  adminApps?: string[];
  userAdminApps?: string[];
  wfAdminApps?: string[];
  roles: UserRole[];
  profiles: Record<string, string>;
  emailAddress: string;
  patchAdminApps?: string[];
  viewOnlyApps?: string[];
  otherAttributes?: string;
  userId?: number;
}

export const userAtom = atom<UserSession | undefined>({
  path: ['session', 'user'],
  defaultValue: undefined,
});

export const userValidatedAtom = atom({
  path: ['session', 'validated'],
  defaultValue: false,
});

export const isDeveloperSelector = selector({
  path: ['session', 'isDevSel', ':appUid'],
  get: ({ get, params }) => {
    const user = get(userAtom);
    const { appUid } = params;
    return !!user?.devApps?.includes(appUid as string);
  },
});

export const isViewOnlySelector = selector({
  path: ['session', 'isViewOnlySel', ':appUid'],
  get: ({ get, params }) => {
    const user = get(userAtom);
    const { appUid } = params;
    const readOnlyDev = !!user?.viewOnlyApps?.includes(appUid as string);
    return readOnlyDev;
  },
});

export const isAdministratorSelector = selector({
  path: ['session', 'isAdminSel', ':appUid'],
  get: ({ get, params }) => {
    const user = get(userAtom);
    const { appUid } = params;
    return !!user?.adminApps?.includes(appUid as string);
  },
});

export function useIsAdministrator(appUid: string): boolean {
  return useStateXSelector(isAdministratorSelector, { params: { appUid } });
}

export function useIsDeveloper(appUid: string) {
  return useStateXSelector(isDeveloperSelector, { params: { appUid } });
}

export function useIsViewOnlyUser(appUid: string) {
  return useStateXSelector(isViewOnlySelector, { params: { appUid } });
}

export function getProfileValue(
  platform: Platform,
  appUid: string,
  profile: string,
): string | null {
  const user = platform.getter(userAtom);
  if (!user) return null;
  // Profile value to be checked in the following order
  // 1. app.profile.$ user level profile value
  // 2. app.profile.cloudio any role level in cloudio app for which the user have access to
  // 3. Any other application's role level (if multiple roles,
  //    the one with lower IO_USER_ROLES.SEQ_NO) will be used
  // 4. Profile level default value i.e. '/'
  let value =
    user.profiles[`cloudio.${profile}.cloudio.$`] ?? // user level value at cloudio app
    user.profiles[`cloudio.${profile}.${appUid}.$`] ?? // user level value at respective app
    user.profiles[`cloudio.${profile}.cloudio`] ?? // role/App level value at cloudio app
    user.profiles[`cloudio.${profile}.${appUid}`] ?? // role/App level value at respective app
    user.profiles[`${appUid}.${profile}.${appUid}.$`] ?? // user level value of custom profiles
    user.profiles[`${appUid}.${profile}.${appUid}`]; // role/App level value of custom profiles
  if (isEmpty(value)) {
    const key = Object.keys(user.profiles).find(
      (k) =>
        k.startsWith(`cloudio.${profile}.cloudio`) ||
        k.startsWith(`cloudio.${profile}.${appUid}`) ||
        k.startsWith(`${appUid}.${profile}.${appUid}`),
    );
    if (key) {
      value = user.profiles[key];
    }
  }
  if (isEmpty(value)) {
    value =
      user.profiles[`cloudio.${profile}`] ??
      user.profiles[`${appUid}.${profile}`];
  }
  return value;
}

export function getUser(platform: Platform) {
  return platform.getter(userAtom);
}

export function getUserName(platform: Platform): string {
  return getUser(platform)?.userName ?? 'GUEST';
}

export function getHomePage(platform: Platform) {
  const user = getUser(platform);
  if (!user) return '/';
  // User home page to be loaded in the following order
  // 1. HOME_PAGE.$ user level profile value
  // 2. HOME_PAGE.cloudio any role level in cloudio app for which the user have access to
  // 3. Any other application's role level (if multiple roles,
  //    the one with lower IO_USER_ROLES.SEQ_NO) will be used
  // 4. Profile level default value i.e. '/'
  let homePage =
    user.profiles['cloudio.HOME_PAGE.$'] ??
    user.profiles['cloudio.HOME_PAGE.cloudio'];
  if (isEmpty(homePage)) {
    const key = Object.keys(user.profiles)
      .sort()
      .find((k) => k.startsWith('cloudio.HOME_PAGE.'));
    if (key) {
      homePage = user.profiles[key];
    }
  }
  if (isEmpty(homePage)) {
    homePage = user.profiles['cloudio.HOME_PAGE'] || '/';
  }
  if (homePage.startsWith('#')) {
    homePage = homePage.substring(1);
  }
  if (!homePage.startsWith('/')) {
    homePage = `/${homePage}`;
  }
  return homePage;
}

export function getAppHomePage(platform: Platform, app: string): string | null {
  const user = getUser(platform);
  if (!user) return null;
  return (
    user.profiles[`cloudio.HOME_PAGE.${app}.$`] ??
    user.profiles[`cloudio.HOME_PAGE.${app}`] ??
    null
  );
}

export function setDeveloperApps(platform: Platform, devApps: string[]) {
  const user = getUser(platform);
  if (user) {
    platform.setter(userAtom, { ...user, devApps });
  }
}

export function setAdministratorApps(platform: Platform, adminApps: string[]) {
  const user = getUser(platform);
  if (user) {
    platform.setter(userAtom, { ...user, adminApps });
  }
}

export function setViewOnlyApps(platform: Platform, viewOnlyApps: string[]) {
  const user = getUser(platform);
  if (user) {
    platform.setter(userAtom, { ...user, viewOnlyApps });
  }
}

export function setUserAdministratorApps(
  platform: Platform,
  userAdminApps: string[],
) {
  const user = getUser(platform);
  if (user) {
    platform.setter(userAtom, { ...user, userAdminApps });
  }
}

export function setWfAdministratorApps(
  platform: Platform,
  wfAdminApps: string[],
) {
  const user = getUser(platform);
  if (user) {
    platform.setter(userAtom, { ...user, wfAdminApps });
  }
}

export function setUserRoles(platform: Platform, roles: UserRole[] = []) {
  const user = getUser(platform);
  if (user) {
    platform.setter(userAtom, { ...user, roles });
  }
}

export function setPatchAdministratorApps(
  platform: Platform,
  patchAdminApps: string[] = [],
) {
  const user = getUser(platform);
  if (user) {
    platform.setter(userAtom, { ...user, patchAdminApps });
  }
}

export function hasRole(
  platform: Platform,
  appUid: string,
  roleUid: string,
): boolean {
  const user = getUser(platform);
  return !!user?.roles?.find(
    (ur) => ur.appUid === appUid && ur.roleUid === roleUid,
  );
}

export function isDeveloper(platform: Platform, appUid: string): boolean {
  const user = getUser(platform);
  return !!user?.devApps?.includes(appUid);
}

export function isAdministrator(platform: Platform, appUid: string): boolean {
  const user = getUser(platform);
  return !!user?.adminApps?.includes(appUid);
}

export function isUserAdministrator(
  platform: Platform,
  appUid: string,
): boolean {
  const user = getUser(platform);
  return !!user?.userAdminApps?.includes(appUid);
}

export function isWfAdministrator(platform: Platform, appUid: string): boolean {
  const user = getUser(platform);
  return !!user?.wfAdminApps?.includes(appUid);
}

export function isPatchAdministrator(
  platform: Platform,
  appUid: string,
): boolean {
  const user = getUser(platform);
  return !!user?.patchAdminApps?.includes(appUid);
}

export function isViewOnlyDeveloper(
  platform: Platform,
  appUid: string,
): boolean {
  const user = getUser(platform);
  return !!user?.viewOnlyApps?.includes(appUid);
}

export function useSidebarVisible(appUid: string) {
  const platform = usePlatform();
  const hideSBProfile =
    getProfileValue(platform, appUid, 'HIDE_SIDEBAR_PANEL') === 'Y';
  const hideSidebar = useStateXAtomValue(hideSidebarAtom);
  // return !hideSidebar;
  const hasPlatformRole =
    isDeveloper(platform, appUid) ||
    isAdministrator(platform, appUid) ||
    isUserAdministrator(platform, appUid) ||
    isWfAdministrator(platform, appUid) ||
    isViewOnlyDeveloper(platform, appUid) ||
    isPatchAdministrator(platform, appUid);
  const show = hideSBProfile ? hasPlatformRole : true;
  return !hideSidebar && show;
}

export function getSessionId(platform: Platform) {
  const user = getUser(platform);
  return user?.sessionId;
}

export const guestUser: UserSession = {
  displayName: 'Guest User',
  isAuthenticated: false,
  orgUid: 'GUEST',
  sessionId: 'GUEST',
  userName: 'guest@cloudio.io',
  x: 'GUEST',
  jwt: 'GUEST',
  status: 'OK',
  roles: [],
  profiles: {},
  emailAddress: 'guest@cloudio.io',
};

/**
 * Use this hook to get the current user session
 * @returns {UserSession} UserSession
 */
export default function useUser(): UserSession {
  const user = useStateXAtomValue(userAtom);
  return user ?? guestUser;
}

export function getSessionProperty(
  platform: Platform,
  prop: keyof UserSession | 'role',
): unknown {
  if (prop === 'role') {
    return platform.getter(routeAtom).role;
  }

  const user = getUser(platform);
  return user
    ? user[prop as keyof UserSession]
    : guestUser[prop as keyof UserSession];
}

export function useUserDateFormat(): string {
  const _user = useUser();
  const dateFormat = useStateXAtomValue(userDateFormatAtom, {
    params: { userName: _user.userName },
  });
  return dateFormat;
}

export function useUserDateTimeFormat(): string {
  const _user = useUser();
  const dateTimeFormat = useStateXAtomValue(userDateTimeFormatAtom, {
    params: { userName: _user.userName },
  });

  return dateTimeFormat;
}

export function useUserCSVDateFormat(): string {
  const _user = useUser();
  const dateFormat = useStateXAtomValue(userCSVDateFormatAtom, {
    params: { userName: _user.userName },
  });
  return dateFormat;
}

export function useUserCSVDateTimeFormat(): string {
  const _user = useUser();
  const dateTimeFormat = useStateXAtomValue(userCSVDateTimeFormatAtom, {
    params: { userName: _user.userName },
  });

  return dateTimeFormat;
}

export function useUserTimeZone(): string {
  const _user = useUser();
  const userTimeZone = useStateXAtomValue(userTimeZoneAtom, {
    params: { userName: _user.userName },
  });
  return userTimeZone;
}

export function useNumberFormat(): string {
  const _user = useUser();
  const numberFormat = useStateXAtomValue(userNumberFormatAtom, {
    params: { userName: _user.userName },
  });
  return numberFormat;
}

export interface UserSetup {
  numberFormat: string;
  userTimeZone: string;
  dateTimeFormat: string;
  dateFormat: string;
}

export function useUserSetup(): UserSetup {
  const _user = useUser();
  const numberFormat = useStateXAtomValue(userNumberFormatAtom, {
    params: { userName: _user.userName },
  });
  const userTimeZone = useStateXAtomValue(userTimeZoneAtom, {
    params: { userName: _user.userName },
  });
  const dateTimeFormat = useStateXAtomValue(userDateTimeFormatAtom, {
    params: { userName: _user.userName },
  });
  const dateFormat = useStateXAtomValue(userDateFormatAtom, {
    params: { userName: _user.userName },
  });
  return useMemo(
    () => ({
      numberFormat,
      userTimeZone,
      dateTimeFormat,
      dateFormat,
    }),
    [dateFormat, dateTimeFormat, numberFormat, userTimeZone],
  );
}

export async function setupUserAccess(platform: Platform, userName: string) {
  const data: RootRequest<GenericDataRecord> = {
    devs: { ds: 'IoAppDevelopersV', query: { limit: 2000 } },
    admins: { ds: 'IoAppAdministratorsV', query: { limit: 2000 } },
    viewOnly: { ds: 'IoAppViewersV', query: { limit: 2000 } },
    userAdmins: {
      ds: 'IoUserAdministratorsV',
      query: { limit: 2000 },
    },
    wfAdmins: {
      ds: 'IoWfAdministratorsV',
      query: { limit: 2000 },
    },
    roles: {
      ds: 'IoUserActiveRolesV',
      query: {
        limit: 2000,
        projection: { appUid: 1, roleUid: 1 },
      },
    },
    patchAdmins: {
      ds: 'IoPatchAdministratorsV',
      query: { limit: 2000 },
    },
  };
  const [_promise] = platform.sendRequest(
    {
      type: 'Post',
      data,
    },
    { appUid: 'cloudio' },
  );

  const rr = await _promise;
  if (rr.status === 'success') {
    const rows = rr.data.devs.data;
    if (Array.isArray(rows)) {
      const apps = (rows as { appUid: string }[]).map((row) => row.appUid);
      setDeveloperApps(platform, apps);
    }
    const admins = rr.data.admins.data;
    if (Array.isArray(admins)) {
      const apps = (admins as { appUid: string }[]).map((row) => row.appUid);
      setAdministratorApps(platform, apps);
    }
    const userAdmins = rr.data.userAdmins.data;
    if (Array.isArray(userAdmins)) {
      const apps = (userAdmins as { appUid: string }[]).map(
        (row) => row.appUid,
      );
      setUserAdministratorApps(platform, apps);
    }
    const wfAdmins = rr.data.wfAdmins.data;
    if (Array.isArray(wfAdmins)) {
      const apps = (wfAdmins as { appUid: string }[]).map((row) => row.appUid);
      setWfAdministratorApps(platform, apps);
    }
    const roles = rr.data.roles.data as UserRole[];
    if (Array.isArray(roles)) {
      setUserRoles(platform, roles);
    }
    const patchAdmins = rr.data.patchAdmins.data;
    if (Array.isArray(patchAdmins)) {
      const apps = (patchAdmins as { appUid: string }[]).map(
        (row) => row.appUid,
      );
      setPatchAdministratorApps(platform, apps);
    }
    const viewOnly = rr.data.viewOnly.data;
    if (Array.isArray(viewOnly)) {
      const apps = (viewOnly as { appUid: string }[]).map((row) => row.appUid);
      setViewOnlyApps(platform, apps);
    }
  }
}
